import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUCT_KEYS, Products, ProductsChild, ProductsOption } from '@type/productType';
import { CallGetProducts, CallPutIsDisplay, CallPutSoldout } from '@library/request/callProduct';
import { useSearchParams } from 'react-router-dom';
import CommonSwitchButton from '@components/molecules/button/SwitchButton';
import Spinner from '@components/molecules/Spinner';
import { useMenuPermission } from '@src/recoil/permission';

// 메뉴 노출 · 품절 설정
const MenuExpose = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const productID = searchParams.get('productID');

  const [loading, setLoading] = useState(false);

  const { menuPermission } = useMenuPermission(storeId);

  // isNoAuth4 로직과 동기화: 노출 AND 품절 권한 체크
  // switch문 유지 이유: 가격 옵션별 노출/품절 개별 제어 필요
  const btnDisable = useMemo(() => {
    if (!menuPermission.isFetch || !menuPermission.data?.[0]) {
      return { displayBtn: false, soldoutBtn: false };
    }
    const simplePermission = menuPermission.data[0];

    switch (simplePermission.permissionType) {
      case 0: // 권한없음:0
        return { displayBtn: true, soldoutBtn: true };
      case 1: // 전체권한:1
        return { displayBtn: false, soldoutBtn: false };
      case 2: // 수정권한:2
        if (simplePermission.soldOut === 1 && simplePermission.display === 1) {
          return { displayBtn: false, soldoutBtn: false };
        }
        if (simplePermission.display === 1) {
          return { displayBtn: false, soldoutBtn: true };
        }
        if (simplePermission.soldOut === 1) {
          return { displayBtn: true, soldoutBtn: false };
        }
        break;
      default:
        return { displayBtn: false, soldoutBtn: false };
    }
  }, [menuPermission]);

  // 메뉴 조회 API
  const { data: productDetail, isFetching: isQueryLoading } = useQuery<Products>(
    [PRODUCT_KEYS.PRODUCT, storeId, productID],
    {
      enabled: !!storeId && !!productID,
      queryFn: async () => {
        try {
          const response = await CallGetProducts(storeId, productID);
          if (response?.result.code !== 1) return;
          const { data } = response;
          return data;
        } catch (error) {
          console.log('error', error);
          throw error;
        }
      },
    },
  );

  // 노출/비노출 변경 API
  const { mutate: isDisplayMutation } = useMutation(
    ({ param }: { param: any; title: string }) => CallPutIsDisplay(storeId, productID, param),
    {
      onMutate: () => setLoading(true),
      onSuccess: (response, variables) => {
        const { result } = response;
        const { title } = variables;
        if (result.code === 1) {
          alert(`${title}로 설정되었습니다.`);
          queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]);
          queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
        } else {
          alert(result.message);
        }
      },
      onError: (error) => console.log('error:', error),
      onSettled: () => setLoading(false),
    },
  );

  // 판매중/품절 변경 API
  const { mutate: isSoldoutMutation } = useMutation(
    ({
      path,
      productID,
      type,
    }: {
      path: 'products' | 'options';
      productID: string;
      title: string;
      type: 'sale' | 'soldout';
    }) => CallPutSoldout(storeId, path, productID, type),
    {
      onMutate: () => setLoading(true),
      onSuccess: (response, variables) => {
        const { result } = response;
        const { title } = variables;
        if (result.code === 1) {
          alert(`${title} 되었습니다.`);
          queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]);
          queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
        } else {
          alert(result.message);
        }
      },
      onError: (error) => console.log('error:', error),
      onSettled: () => setLoading(false),
    },
  );

  // 모두 품절인 상태에서, 가격옵션 중 하나라도 판매중으로 바꾼경우
  // => 메뉴를 판매중으로 변경 && 변경한 갸격 옵션을 제외한 가격 옵션들을 품절로 변경
  const handlePartialSale = async (options: ProductsChild[]) => {
    try {
      setLoading(true);
      const { result } = await CallPutSoldout(storeId, 'products', productID, 'sale');
      if (result.code !== 1) {
        throw new Error(result.message || '실패하였습니다.');
      }
      const optionResponses = await Promise.all(
        options.map((opt) => CallPutSoldout(storeId, 'options', opt.optionID, 'soldout')),
      );
      const failed = optionResponses.find((res) => res.result?.code !== 1);
      if (failed) {
        const message = failed.result?.message || '실패했습니다.';
        alert(message);
      } else {
        alert('품절 해제되었습니다.');
        queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]);
      }
    } catch (e) {
      console.log('error:', e);
      alert(e?.message || '처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 가격옵션의 children 리턴
  const getPriceOptionChildren = (data: ProductsOption[]) => {
    return data?.find((option) => Number(option.isPriceOption) === 1)?.children || [];
  };

  // 노출/비노출 변경
  const saveProductDisplay = (e: React.ChangeEvent<HTMLInputElement>, optId: string) => {
    const { checked, id } = e.target;
    const isDisplay = checked ? 1 : 0;
    const msg = isDisplay == 1 ? '노출' : '비노출';
    const isMainToggle = id === 'isDisplay';

    if (!window.confirm(`${msg}로 설정하시겠습니까?`)) return;

    const buildDisplayMap = (): Record<string, number> => {
      if (isMainToggle) {
        return priceOptChild?.reduce((acc, child: ProductsChild) => {
          acc[child.optionID] = isDisplay;
          return acc;
        }, {} as Record<string, number>);
      }
      return { [optId]: isDisplay };
    };

    const applyDisplayUpdates = (options: ProductsOption[], displayMap: Record<string, number>) => {
      return options.map((group) => ({
        ...group,
        children: group.children.map((child: ProductsChild) => ({
          ...child,
          isDisplay: displayMap[child.optionID]?.toString() ?? child.isDisplay,
        })),
      }));
    };

    const updatedOptions = applyDisplayUpdates(productDetail.options, buildDisplayMap());
    const optionIsDisplay = getPriceOptionChildren(updatedOptions).some((child) => Number(child.isDisplay)) ? 1 : 0;

    const param = {
      product: {
        isDisplay: isMainToggle ? isDisplay : optionIsDisplay,
        options: updatedOptions,
      },
    };

    isDisplayMutation({ param, title: msg }); // API 호출
  };

  // 판매중/품절 변경
  const saveProductStatus = (e: React.ChangeEvent<HTMLInputElement>, optId: string) => {
    const { checked, id } = e.target;
    const soldout = checked ? 0 : 1; // (0:판매중, 1:품절)
    const isMainToggle = id === 'status';
    const msg = {
      0: ['품절 설정을 해제', '품절 해제'],
      1: ['품절로 설정', '품절로 설정'],
    };

    if (!window.confirm(`${msg[soldout][0]}하시겠습니까?`)) {
      return;
    }

    const otherOptions = priceOptChild?.filter((opt: ProductsChild) => opt.optionID !== optId);
    // 변경된 옵션 포함한 모든 옵션이 품절 or 판매중인 경우
    const isAllSame = otherOptions.every(
      (opt: ProductsChild) =>
        (Number(opt.status) === 4 && soldout === 1) || (Number(opt.status) === 1 && soldout === 0),
    );

    console.log('isAllSame', isAllSame);

    if (Number(productDetail?.status) !== 1 && !isMainToggle && soldout === 0) {
      // 모두 품절인 상태에서, 가격옵션 중 하나라도 판매중으로 바꾼경우
      handlePartialSale(otherOptions as ProductsChild[]); // API 호출
    } else {
      const type = checked ? 'sale' : 'soldout';
      const path = isMainToggle || isAllSame ? 'products' : 'options';
      const selectedID = isMainToggle || isAllSame ? productID : optId;

      isSoldoutMutation({ path, productID: selectedID, type, title: msg[soldout][1] }); // API 호출
    }
  };

  // productDetail.options의 가격옵션
  const priceOptChild = useMemo(() => {
    if (!productDetail?.options) return;
    return getPriceOptionChildren(productDetail?.options);
  }, [productDetail]);


  return (
    <article id="mArticle" className="art-product menu-expose">
      <div className="view-product">
        <div className="box-product">
          <strong id="name" className="lab-product-status">
            {productDetail?.name}
          </strong>
          <div className="group-switch-check">
            <CommonSwitchButton
              id="isDisplay"
              check={Number(productDetail?.isDisplay) === 1}
              checkLabel="노출"
              unCheckLabel="비노출"
              size="medium"
              disabled={btnDisable.displayBtn}
              onChange={(e) => saveProductDisplay(e, productDetail?.ID)}
            />
            <CommonSwitchButton
              id="status"
              check={Number(productDetail?.status) === 1}
              checkLabel="판매중"
              unCheckLabel="품절"
              size="medium"
              disabled={btnDisable.soldoutBtn}
              onChange={(e) => saveProductStatus(e, productDetail?.ID)}
            />
          </div>
          {productDetail?.options?.length > 0 && (
            <ul className="box-switch-check">
              {priceOptChild?.map((child: ProductsChild) => {
                const hasOptionCoupon = Number(child.menuCouponCount) > 0;
                const { name, isDisplay, status, optionID } = child;
                return (
                  <li key={optionID}>
                    {hasOptionCoupon && <span className="badge-coupon">메뉴 쿠폰</span>}
                    <strong className="lab-switch-check">{name || '기본'}</strong>
                    <div className="group-switch-check">
                      <CommonSwitchButton
                        check={Number(isDisplay) === 1}
                        checkLabel="노출"
                        unCheckLabel="비노출"
                        size="small"
                        disabled={btnDisable.displayBtn}
                        onChange={(e) => saveProductDisplay(e, optionID)}
                      />
                      <CommonSwitchButton
                        check={Number(status) === 1}
                        checkLabel="판매중"
                        unCheckLabel="품절"
                        size="small"
                        disabled={btnDisable.soldoutBtn}
                        onChange={(e) => saveProductStatus(e, optionID)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <ul className="txt-muted">
          <li>비노출로 설정한 메뉴 및 옵션은 고객 주문화면에 게시되지 않습니다.</li>
          <li>
            품절 설정 시 설정한 당일 영업시간 마감까지 유지되며, 다음날 자동 해제 됩니다. (24시간 운영 매장은 오전 5시에
            자동으로 품절 해제됩니다.)
          </li>
        </ul>
      </div>
      <Spinner show={loading || isQueryLoading} />
    </article>
  );
};

export default MenuExpose;
